import { CONTENT_TYPE } from '../config'
import { isPdf } from '../extras/utils'
import { downloadVehicleHeroShot } from './offlineHeroShot'
import {
  cachePdfUrl,
  getDownloadMeta,
  removeDownloadType,
  saveDownloadMeta,
  saveVehicleContents,
  uncachePdfUrl,
} from './offlineStorage'

export const COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE = 'component-manual-pdfs'
export const COMPONENT_MANUAL_CATEGORY = 'Component Manual'

function buildPdfEntry(topic) {
  return {
    resourceKey: topic.resourceKey,
    title: topic.title,
    url: topic.pdfUrl || topic.bodyPdf?.url,
    tags: topic.pdfTags || topic.tags || [],
  }
}

function buildContentsSnapshot(contents, publications) {
  return {
    topics: contents?.topics ? contents.topics.toJS() : [],
    publications: publications || (contents?.publications ? contents.publications.toJS() : []),
    folders: contents?.folders ? contents.folders.toJS() : [],
    heroShots: contents?.heroShots
      ? (contents.heroShots.toJS ? contents.heroShots.toJS() : contents.heroShots)
      : [],
    ownersManualPdfs: [],
  }
}

function getIndividualPdfDownload(meta) {
  return meta?.downloads?.[COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE] || null
}

/**
 * Builds a publication-shaped object from a stored offline PDF entry
 *
 * @param {object} entry stored PDF metadata
 * @returns {object}
 */
export function publicationFromStoredPdfEntry(entry) {
  return {
    type: CONTENT_TYPE.PUBLICATION,
    publicationType: 'DEFAULT',
    resourceKey: entry.resourceKey,
    title: entry.title,
    tags: entry.tags || [],
    bodyPdf: { url: entry.url },
  }
}

function hasFullComponentManualDownload(meta) {
  return !!(
    meta?.downloads?.['all-content']?.items?.includes(COMPONENT_MANUAL_CATEGORY) ||
    meta?.downloads?.['owners-manual']?.items?.includes(COMPONENT_MANUAL_CATEGORY)
  )
}

/**
 * Returns resource keys for individually downloaded component manual PDFs
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<Set<string>>}
 */
export async function getIndividualComponentManualPdfKeys(vehicleId) {
  const meta = await getDownloadMeta(vehicleId)
  const individual = getIndividualPdfDownload(meta)
  const keys = new Set()

  if (individual?.pdfs) {
    individual.pdfs.forEach(pdf => {
      if (pdf.resourceKey) keys.add(pdf.resourceKey)
    })
  }

  return keys
}

/**
 * Returns resource keys for offline-available component manual PDFs
 *
 * @param {string} vehicleId vehicle identifier
 * @param {Array} publications component manual publications
 * @returns {Promise<Set<string>>}
 */
export async function getOfflineComponentManualPdfKeys(vehicleId, publications = []) {
  const meta = await getDownloadMeta(vehicleId)
  const keys = new Set()
  const individual = getIndividualPdfDownload(meta)

  if (individual?.pdfs) {
    individual.pdfs.forEach(pdf => {
      if (pdf.resourceKey) keys.add(pdf.resourceKey)
    })
  }

  if (hasFullComponentManualDownload(meta)) {
    publications.filter(isPdf).forEach(pdf => {
      if (pdf.resourceKey) keys.add(pdf.resourceKey)
    })
  }

  return keys
}

/**
 * Returns component manual PDFs visible in offline mode
 *
 * @param {string} vehicleId vehicle identifier
 * @param {Array} publications component manual publications
 * @returns {Promise<{ keys: Set<string>, pdfs: Array }>}
 */
export async function getOfflineVisibleComponentManualPdfs(vehicleId, publications = []) {
  const meta = await getDownloadMeta(vehicleId)
  const keys = await getOfflineComponentManualPdfKeys(vehicleId, publications)
  const pdfsByKey = new Map()

  publications.filter(isPdf).forEach(pdf => {
    if (keys.has(pdf.resourceKey)) {
      pdfsByKey.set(pdf.resourceKey, pdf)
    }
  })

  const individual = getIndividualPdfDownload(meta)
  if (individual?.pdfs) {
    individual.pdfs.forEach(entry => {
      if (entry.resourceKey && keys.has(entry.resourceKey) && !pdfsByKey.has(entry.resourceKey)) {
        pdfsByKey.set(entry.resourceKey, publicationFromStoredPdfEntry(entry))
      }
    })
  }

  return { keys, pdfs: [...pdfsByKey.values()] }
}

/**
 * Finds a component manual PDF for offline viewing
 *
 * @param {string} vehicleId vehicle identifier
 * @param {string} resourceKey publication resource key
 * @param {Array} publications plain publication list
 * @returns {Promise<object|null>}
 */
export async function findOfflineComponentManualPdf(vehicleId, resourceKey, publications = []) {
  if (!vehicleId || !resourceKey) return null

  const fromPublications = publications.filter(isPdf).find(pdf => pdf.resourceKey === resourceKey)
  if (fromPublications) return fromPublications

  const meta = await getDownloadMeta(vehicleId)
  const individual = getIndividualPdfDownload(meta)
  const stored = individual?.pdfs?.find(pdf => pdf.resourceKey === resourceKey)

  if (stored) {
    return publicationFromStoredPdfEntry(stored)
  }

  const keys = await getOfflineComponentManualPdfKeys(vehicleId, publications)
  if (!keys.has(resourceKey)) return null

  return publications.filter(isPdf).find(pdf => pdf.resourceKey === resourceKey) || null
}

/**
 * Downloads a single component manual PDF for offline use
 *
 * @param {string} vehicleId vehicle identifier
 * @param {object} topic component manual list item
 * @param {object} options download options
 * @param {object} options.contents Redux contents slice
 * @param {Array} options.publications plain publication list
 * @param {object} options.vehicleInfo vehicle metadata
 * @returns {Promise<void>}
 */
export async function downloadComponentManualPdf(
  vehicleId,
  topic,
  { contents, publications, vehicleInfo } = {}
) {
  const entry = buildPdfEntry(topic)
  if (!entry.url || !entry.resourceKey) return

  await cachePdfUrl(entry.url)

  const meta = await getDownloadMeta(vehicleId)
  const existing = getIndividualPdfDownload(meta)
  const pdfs = [...(existing?.pdfs || [])]
  const pdfUrls = new Set(existing?.pdfUrls || [])

  const index = pdfs.findIndex(pdf => pdf.resourceKey === entry.resourceKey)
  if (index >= 0) {
    pdfs[index] = entry
  } else {
    pdfs.push(entry)
  }
  pdfUrls.add(entry.url)

  if (contents && publications) {
    await saveVehicleContents(
      vehicleId,
      buildContentsSnapshot(contents, publications),
      vehicleInfo
    )
  }

  const heroShot = vehicleInfo
    ? await downloadVehicleHeroShot(vehicleInfo, contents?.heroShots)
    : meta?.heroShot || null

  await saveDownloadMeta(
    vehicleId,
    COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE,
    [COMPONENT_MANUAL_CATEGORY],
    {
      objectKeys: [],
      pdfUrls: [...pdfUrls],
      pdfs,
    },
    vehicleInfo || meta?.vehicleInfo || null,
    heroShot
  )
}

/**
 * Removes a single component manual PDF from offline storage
 *
 * @param {string} vehicleId vehicle identifier
 * @param {string} resourceKey publication resource key
 * @param {string} pdfUrl cached PDF URL
 * @returns {Promise<void>}
 */
export async function removeComponentManualPdf(vehicleId, resourceKey, pdfUrl) {
  const meta = await getDownloadMeta(vehicleId)
  const existing = getIndividualPdfDownload(meta)
  if (!existing) return

  const pdfs = (existing.pdfs || []).filter(pdf => pdf.resourceKey !== resourceKey)
  const pdfUrls = (existing.pdfUrls || []).filter(url => url !== pdfUrl)

  if (pdfUrl) {
    await uncachePdfUrl(pdfUrl)
  }

  if (!pdfs.length) {
    await removeDownloadType(vehicleId, COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE)
    return
  }

  await saveDownloadMeta(
    vehicleId,
    COMPONENT_MANUAL_PDF_DOWNLOAD_TYPE,
    [COMPONENT_MANUAL_CATEGORY],
    {
      objectKeys: [],
      pdfUrls,
      pdfs,
    },
    meta?.vehicleInfo || null,
    meta?.heroShot || null
  )
}
